import React from "react";
import { useFragment } from "react-relay";

import {
	faClock,
	faEdit,
	faEllipsisV,
	faHashtag,
	faLink,
	faPaperPlane,
	faTrashCan,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faSpinner } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { commitMutation, graphql } from "relay-runtime";

import { useID } from "~/lib/hooks";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { eventEmitter as recordingsEventEmitter } from "~/recordings/events";
import { Button, toast, useModals } from "~/ui";
import { IconButton, Input, Modal } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { BillingContext } from "../account/Billing/BillingContext";
import { AppContext } from "../common";
import { addOrRemoveRecordingOrClipFromFavourites } from "../recordings/LibraryPostActions";
import { PlayButton } from "./MeetingSummaryBanner";
import { openShareClipModal } from "./ShareClipModal";
import { copyToClipboard, formatDuration, useGetURL } from "./utils";

import type { ClipsPanel_ClipCard_clip$key } from "~/__generated__/ClipsPanel_ClipCard_clip.graphql";
import type { ClipsPanel_ClipSummary_clip$key } from "~/__generated__/ClipsPanel_ClipSummary_clip.graphql";
import type { ClipsPanel_DeleteClipModal_clip$key } from "~/__generated__/ClipsPanel_DeleteClipModal_clip.graphql";
import type { ClipsPanel_meetingRecording$key } from "~/__generated__/ClipsPanel_meetingRecording.graphql";
import type { ClipsPanel_RenameClipModal_clip$key } from "~/__generated__/ClipsPanel_RenameClipModal_clip.graphql";
import type { ClipsPanelDeleteClipMutation } from "~/__generated__/ClipsPanelDeleteClipMutation.graphql";
import type { ClipsPanelRenameClipMutation } from "~/__generated__/ClipsPanelRenameClipMutation.graphql";

import styles from "./css/ClipsPanel.module.css";

const renameClipMutation = graphql`
	mutation ClipsPanelRenameClipMutation($clipId: ID!, $title: String!) @raw_response_type {
		renameClip(clipId: $clipId, title: $title) {
			clip {
				id
				title
				lastEditedBy {
					id
					fullName
				}
			}
			error
		}
	}
`;

const deleteClipMutation = graphql`
	mutation ClipsPanelDeleteClipMutation($clipId: ID!) {
		deleteClip(clipId: $clipId) {
			meetingRecording {
				...ClipsPanel_meetingRecording
			}
		}
	}
`;

export function RenameClipModal({
	onClose,
	clip: clipKey,
}: {
	onClose: () => void;
	clip: ClipsPanel_RenameClipModal_clip$key;
}) {
	const titleFieldId = useID();

	const clip = useFragment(
		graphql`
			fragment ClipsPanel_RenameClipModal_clip on MeetingRecordingClip {
				id
				title
			}
		`,
		clipKey,
	);

	const [newTitle, setNewTitle] = React.useState(clip.title);
	const [error, setError] = React.useState<string | null>(null);

	const { user } = React.useContext(AppContext);
	const { analyticsFingerprint } = React.useContext(BillingContext);

	const onChangeNewTitle = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setError(null);
		setNewTitle(e.target.value);
	}, []);

	const onSave = React.useCallback(() => {
		commitMutation<ClipsPanelRenameClipMutation>(environment, {
			mutation: renameClipMutation,
			variables: {
				clipId: clip.id,
				title: newTitle,
			},
			optimisticResponse: {
				renameClip: {
					clip: {
						...clip,
						lastEditedBy: { id: user?.id ?? "", fullName: user?.fullName ?? "" },
						title: newTitle,
					},
					error: null,
				},
			},
			onCompleted: (response, errors) => {
				if (errors) {
					toast.error("Something went wrong, please try again later", { title: "Failed to save clip title" });
					return;
				}

				if (response.renameClip?.error) {
					setError(response.renameClip.error);
					return;
				}
				dispatchAnalyticsTrack("[AI Copilot] Renamed Clip", { fingerprint: analyticsFingerprint });
				toast.success("Successfully updated");
				onClose();
			},
		});
	}, [analyticsFingerprint, clip, newTitle, onClose, user?.fullName, user?.id]);

	return (
		<Modal onClose={onClose}>
			<Modal.Header onClose={onClose} noBottomBorder>
				<Modal.Title>Rename clip</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<label htmlFor={titleFieldId}>
					<p className={styles.renameClipLabel}>Clip name</p>
					<Input
						name="clip_title"
						id={titleFieldId}
						className={cx(styles.renameClipInput, { [styles.error]: error })}
						value={newTitle}
						onChange={onChangeNewTitle}
					/>
					{error && <p className={styles.renameClipError}>{error}</p>}
				</label>
			</Modal.Body>
			<Modal.Footer noTopBorder>
				<Button variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				<Button variant="primary" onClick={onSave} disabled={!!error}>
					Save & close
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export function DeleteClipModal({
	onClose,
	clip: clipKey,
}: {
	onClose: () => void;
	clip: ClipsPanel_DeleteClipModal_clip$key;
}) {
	const { analyticsFingerprint } = React.useContext(BillingContext);

	const clip = useFragment(
		graphql`
			fragment ClipsPanel_DeleteClipModal_clip on MeetingRecordingClip {
				id
				title
			}
		`,
		clipKey,
	);

	const onDelete = React.useCallback(() => {
		commitMutation<ClipsPanelDeleteClipMutation>(environment, {
			mutation: deleteClipMutation,
			variables: {
				clipId: clip.id,
			},
			onCompleted: (response, errors) => {
				if (errors) {
					toast.error("Failed to delete clip");
					return;
				}
				dispatchAnalyticsTrack("[AI Copilot] Deleted Clip", { fingerprint: analyticsFingerprint });
				toast.success("Successfully deleted clip");
				onClose();
				recordingsEventEmitter.emit("Recordings.refetch");
			},
		});
	}, [analyticsFingerprint, clip.id, onClose]);

	return (
		<Modal onClose={onClose}>
			<Modal.Header onClose={onClose} noBottomBorder>
				<Modal.Title>Delete this clip?</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<p className={styles.deleteModalCopy}>
					<strong>{clip.title}</strong> will be <strong>permanently deleted</strong> and won&apos;t be available for
					playback or reference.
					<br />
					<br />
					This action cannot be undone.
				</p>
			</Modal.Body>
			<Modal.Footer noTopBorder>
				<Button variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				<Button variant="destructive" onClick={onDelete}>
					Delete
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export function ClipSummary({ clip: clipKey }: { clip: ClipsPanel_ClipSummary_clip$key }) {
	const clip = useFragment(
		graphql`
			fragment ClipsPanel_ClipSummary_clip on MeetingRecordingClip {
				id
				title
				meetingRecordedVideoDuration
				summary
			}
		`,
		clipKey,
	);

	return (
		<article className={styles.clipSummaryContainer}>
			<div className={styles.clipSummaryTitleContainer}>
				<h2 className={styles.clipSummaryTitle}>{clip.title}</h2>
				{clip.meetingRecordedVideoDuration && (
					<p className={styles.clipSummaryDuration}>{formatDuration(clip.meetingRecordedVideoDuration)}</p>
				)}
			</div>
			<p className={styles.clipSummaryText}>{clip.summary}</p>
		</article>
	);
}

interface ClipCardProps {
	clip: ClipsPanel_ClipCard_clip$key;
	onClipSelect: (clipId: string) => void;
}

function ClipCard({ onClipSelect, clip: clipKey }: ClipCardProps) {
	const clip = useFragment(
		graphql`
			fragment ClipsPanel_ClipCard_clip on MeetingRecordingClip {
				id
				title
				viewerCanEdit
				meetingRecordedVideoDuration
				meetingRecordedVideoPosterUrl
				meetingRecordedVideoUrl
				summary
				isFavourite
				creator {
					id
					fullName
				}
				lastEditedBy {
					id
					fullName
				}
				...utils_useGetURL_recording
				...ClipsPanel_RenameClipModal_clip
				...ClipsPanel_DeleteClipModal_clip
			}
		`,
		clipKey,
	);

	const modals = useModals();

	const { user } = React.useContext(AppContext);
	const { analyticsFingerprint } = React.useContext(BillingContext);

	const onDeleteClick = React.useCallback(() => {
		if (!clip.viewerCanEdit) return;

		modals.open(({ close }) => <DeleteClipModal onClose={close} clip={clip} />);
	}, [clip, modals]);

	const onSendClick = React.useCallback(() => {
		if (!clip.viewerCanEdit) return;

		openShareClipModal(clip.id, "Send");
	}, [clip]);

	const onShareToChannelClick = React.useCallback(() => {
		if (!clip.viewerCanEdit) return;

		openShareClipModal(clip.id, "Share");
	}, [clip]);

	const onToggleFavourite = React.useCallback(() => {
		addOrRemoveRecordingOrClipFromFavourites({
			newIsFavourite: !clip.isFavourite,
			recordingOrClipId: clip.id,
			videoType: "clip",
			refetch: true,
		});
	}, [clip.id, clip.isFavourite]);

	const getClipURL = useGetURL(clip);
	const onCopyClick = React.useCallback(() => {
		if (!getClipURL) return;
		copyToClipboard(getClipURL());
		dispatchAnalyticsTrack("[AI Copilot] Copied Link to Clip", {
			fingerprint: analyticsFingerprint,
		});
	}, [analyticsFingerprint, getClipURL]);

	const onRenameClick = React.useCallback(() => {
		if (!clip.viewerCanEdit) return;

		modals.open(({ close }) => <RenameClipModal onClose={close} clip={clip} />);
	}, [clip, modals]);

	const onClick = React.useCallback(() => {
		onClipSelect(clip.id);
	}, [clip, onClipSelect]);

	const onMenuClick = React.useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
	}, []);

	const { lastEditedBy, creator } = clip;
	const videoPosterUrl = clip.meetingRecordedVideoPosterUrl;

	if (!clip.meetingRecordedVideoUrl || clip.summary === null) {
		return (
			<div className={styles.skeletonContainer}>
				<div className={styles.skeletonThumbnail}>
					<FontAwesomeIcon icon={faSpinner} spin color="var(--white)" />
				</div>
				<div className={styles.clipInfo}>
					<div>
						<h1 className={styles.clipTitle}>{clip.title}</h1>
						{lastEditedBy?.fullName ? (
							<p className={styles.clipCreator}>
								Last edited by {lastEditedBy.fullName} {user?.id === lastEditedBy?.id ? "(You)" : ""}
							</p>
						) : (
							<p className={styles.clipCreator}>
								Created by {creator?.fullName} {user?.id === creator?.id ? "(You)" : ""}
							</p>
						)}
					</div>
					<p className={styles.clipDuration}>Clip is generating...</p>
				</div>
				<FontAwesomeIcon icon={faEllipsisV} className={styles.skeletonMenu} />
			</div>
		);
	}

	return (
		<div className={styles.clipContainer} onClick={onClick}>
			{videoPosterUrl && (
				<div
					style={{
						background: [
							"no-repeat center/contain linear-gradient(180deg, rgba(89, 89, 89, 0) 0%, rgba(0, 0, 0, 0.5) 100%)",
							`no-repeat center/contain url("${videoPosterUrl.replace(/"/g, '\\"')}")`,
						].join(", "),
					}}
					className={styles.clipThumbnail}
				>
					<PlayButton
						width="11"
						height="12"
						color="rgba(255, 255, 255, 0.75)"
						className={styles.clipThumbnailOverlayPlayButton}
					/>
				</div>
			)}
			<div className={styles.clipInfo}>
				<div>
					<h1 className={styles.clipTitle}>{clip.title}</h1>
					{lastEditedBy?.fullName ? (
						<p className={styles.clipCreator}>
							Last edited by {lastEditedBy.fullName} {user?.id === lastEditedBy?.id ? "(You)" : ""}
						</p>
					) : (
						<p className={styles.clipCreator}>
							Created by {creator?.fullName} {user?.id === creator?.id ? "(You)" : ""}
						</p>
					)}
				</div>
				{clip.meetingRecordedVideoDuration && (
					<p className={styles.clipDuration}>{formatDuration(clip.meetingRecordedVideoDuration)}</p>
				)}
			</div>
			<div onClick={onMenuClick}>
				<Menu>
					<Menu.Trigger>
						<IconButton className={styles.menuTrigger} icon={faEllipsisV} />
					</Menu.Trigger>
					<Menu.Content align="end">
						{clip.viewerCanEdit && (
							<>
								<Menu.Item icon={faPaperPlane} onSelect={onSendClick}>
									Send via email
								</Menu.Item>
								<Menu.Item icon={faHashtag} onSelect={onShareToChannelClick}>
									Share to channel
								</Menu.Item>
							</>
						)}

						<Menu.Item icon={faClock} onSelect={onToggleFavourite}>
							{clip.isFavourite ? "Remove from" : "Add to"} {"Watch later"}
						</Menu.Item>
						<Menu.Item icon={faLink} onSelect={onCopyClick}>
							Copy link
						</Menu.Item>
						{clip.viewerCanEdit && (
							<>
								<Menu.Item icon={faEdit} onSelect={onRenameClick}>
									Rename
								</Menu.Item>
								<Menu.Separator />
								<Menu.Item destructive icon={faTrashCan} onSelect={onDeleteClick}>
									Delete clip
								</Menu.Item>
							</>
						)}
					</Menu.Content>
				</Menu>
			</div>
		</div>
	);
}
interface ClipsPanelProps {
	meetingRecording: ClipsPanel_meetingRecording$key;
	onClipSelect: (clipId: string) => void;
	className?: string;
}

export function ClipsPanel({ meetingRecording, onClipSelect, className }: ClipsPanelProps) {
	const recording = useFragment(
		graphql`
			fragment ClipsPanel_meetingRecording on MeetingRecording {
				id
				clips {
					id
					...ClipsPanel_ClipCard_clip
				}
			}
		`,
		meetingRecording,
	);

	const { clips } = recording;

	// We shouldn't ever run into this case, but we need to check so that
	// TypeScript doesn't complain about clips being null
	if (clips === null || clips.length === 0) {
		return null;
	}

	return (
		<div className={cx(styles.clipListContainer, className)}>
			{clips.map(clip => (
				<ClipCard key={clip.id} clip={clip} onClipSelect={onClipSelect} />
			))}
		</div>
	);
}
